import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { Input } from "../components/ui/input";
import { Search, MapPin, Star, Stethoscope, Scissors, Utensils, GraduationCap, Home, Truck } from "lucide-react";
import { Badge } from "../components/ui/badge";

const CATS = [
    { key: "", label: "All" },
    { key: "veterinary", label: "Vet", icon: Stethoscope },
    { key: "grooming", label: "Grooming", icon: Scissors },
    { key: "food", label: "Food", icon: Utensils },
    { key: "training", label: "Training", icon: GraduationCap },
    { key: "boarding", label: "Boarding", icon: Home },
    { key: "transport", label: "Transport", icon: Truck },
];

export default function ServicesDirectory() {
    const [items, setItems] = useState([]);
    const [q, setQ] = useState("");
    const [cat, setCat] = useState("");
    const [region, setRegion] = useState("");
    useEffect(() => { api.get("/services").then((r) => setItems(r.data)); }, []);
    const regions = useMemo(() => Array.from(new Set(items.map((i) => i.region).filter(Boolean))).sort(), [items]);
    const filt = useMemo(() => items.filter((i) => {
        if (cat && i.category !== cat) return false;
        if (region && i.region !== region) return false;
        if (q && !`${i.name} ${i.description}`.toLowerCase().includes(q.toLowerCase())) return false;
        return true;
    }), [items, q, cat, region]);

    return (
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10" data-testid="services-directory">
            <div className="mb-8">
                <div className="eyebrow mb-3">Partner services · Worldwide</div>
                <h1 className="font-serif text-4xl lg:text-6xl tracking-tight">Vets, groomers,<br />food, and transport.</h1>
            </div>
            <div className="flex flex-wrap gap-3 items-center mb-6">
                <div className="relative flex-1 min-w-[240px] max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--rl-text-3)]" />
                    <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search services…"
                           className="pl-9 border-[var(--rl-border)] h-11 rounded-full" data-testid="services-search" />
                </div>
            </div>
            <div className="flex flex-wrap gap-2 mb-4">
                {CATS.map((c) => {
                    const Icon = c.icon;
                    const active = cat === c.key;
                    return (
                        <button key={c.label} onClick={() => setCat(c.key)}
                                data-testid={`services-cat-${c.label.toLowerCase()}`}
                                className={`px-4 py-2 rounded-full text-sm flex items-center gap-1.5 border ${active ? "bg-[var(--rl-primary)] text-white border-[var(--rl-primary)]" : "border-[var(--rl-border)] text-[var(--rl-text-2)] hover:bg-[var(--rl-cream)]"}`}>
                            {Icon && <Icon className="w-3.5 h-3.5" />} {c.label}
                        </button>
                    );
                })}
            </div>
            <div className="flex flex-wrap gap-2 mb-8" data-testid="services-region-filter">
                <button onClick={() => setRegion("")} className={`px-3 py-1.5 rounded-full text-xs border ${region === "" ? "bg-[var(--rl-text)] text-white border-[var(--rl-text)]" : "border-[var(--rl-border)] text-[var(--rl-text-3)] hover:bg-[var(--rl-cream)]"}`}>All regions</button>
                {regions.map((r) => (
                    <button key={r} onClick={() => setRegion(r)}
                            className={`px-3 py-1.5 rounded-full text-xs border ${region === r ? "bg-[var(--rl-text)] text-white border-[var(--rl-text)]" : "border-[var(--rl-border)] text-[var(--rl-text-3)] hover:bg-[var(--rl-cream)]"}`}>{r}</button>
                ))}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filt.map((s) => (
                    <div key={s.id} className="card-warm rounded-xl overflow-hidden" data-testid={`service-card-${s.id}`}>
                        <div className="aspect-[16/9] bg-[var(--rl-cream)] overflow-hidden">
                            <img src={s.logo_url} alt={s.name} className="w-full h-full object-cover" loading="lazy" />
                        </div>
                        <div className="p-5">
                            <div className="flex items-center justify-between gap-2 mb-1">
                                <h3 className="font-serif text-xl">{s.name}</h3>
                                <div className="flex items-center gap-1 text-sm"><Star className="w-4 h-4 fill-[#e2a900] text-[#e2a900]" /> {s.rating}</div>
                            </div>
                            <Badge variant="outline" className="text-[10px] uppercase tracking-widest mb-2">{s.category}</Badge>
                            {s.partner && <Badge className="bg-[var(--rl-success)] text-white text-[10px] uppercase tracking-widest ml-1 hover:bg-[var(--rl-success)]">Rescue Partner</Badge>}
                            <div className="flex items-center gap-1 text-xs text-[var(--rl-text-3)] mt-2"><MapPin className="w-3 h-3" /> {s.location} <span className="ml-1 text-[10px] uppercase tracking-widest bg-[var(--rl-cream)] px-2 py-0.5 rounded-full">{s.region}</span></div>
                            <p className="text-sm text-[var(--rl-text-2)] mt-2 line-clamp-3">{s.description}</p>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
